import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronRight, ExternalLink } from "lucide-react";
import { breadcrumbSchema, personSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import { useLanguage } from "@/context/LanguageContext";
import { getBiography, getSettings } from "@/lib/api";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Biography() {
  const { data: bio } = useQuery({ queryKey: ["biography"], queryFn: getBiography });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { lang, tr } = useLanguage();

  if (!bio || !bio.sections) return null;

  const sections = bio.sections.filter((s) => s.heading && s.content);
  const heading = (s) => (lang === "kn" && s.heading_kn) || s.heading;
  const content = (s) => (lang === "kn" && s.content_kn) || s.content;
  const updated = (bio.updated_at || "").slice(0, 10);

  return (
    <div data-testid="biography-page" className="bg-white">
      <PageSeo
        slug="biography"
        title={`${bio.title} — Biography | GBA Hoodi Ward 29`}
        description={bio.subtitle}
        keywords="Girish Kumar Naidu biography, wiki, profile, social worker Bengaluru, Hoodi Ward 29"
        image={bio.photo}
        schemas={[
          { "@context": "https://schema.org", "@type": "ProfilePage", mainEntity: personSchema(settings) },
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Biography" }]),
        ]}
      />

      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-20">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-gray-400 mb-8">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gold">{tr("page.bio.overline")}</span>
        </nav>

        {/* Wiki-style title */}
        <h1 className="font-serif text-4xl md:text-5xl text-navy border-b-[3px] border-gray-300 pb-3" data-testid="bio-title">
          {bio.title}
        </h1>
        <p className="mt-3 text-gray-600 italic font-serif text-base md:text-lg leading-relaxed">{bio.subtitle}</p>
        <p className="mt-2 text-xs text-gray-400">
          {tr("page.bio.note")} · {tr("page.bio.updated")}: {updated}
        </p>

        <div className="mt-8 lg:grid lg:grid-cols-12 lg:gap-10">
          {/* Infobox — floats right like Wikipedia */}
          <aside className="lg:col-span-4 lg:order-2 mb-8 lg:mb-0" data-testid="bio-infobox">
            <div className="border border-gray-300 bg-paper text-sm max-w-sm mx-auto lg:mx-0 lg:sticky lg:top-28">
              <div className="bg-navy text-white text-center font-serif font-bold text-lg py-2.5 px-3">
                {bio.title}
              </div>
              {bio.photo && (
                <div className="p-3 pb-1 text-center">
                  <img src={bio.photo} alt={bio.title} className="w-full object-cover border border-gray-200" />
                  <p className="text-xs text-gray-500 mt-1.5 italic">{bio.photoCaption}</p>
                </div>
              )}
              <table className="w-full border-collapse">
                <tbody>
                  {(bio.infobox || []).map((row, i) => (
                    <tr key={`${row.label}-${i}`} className="border-t border-gray-200 align-top">
                      <th className="text-left font-bold text-navy px-3 py-2 w-[38%] text-[13px]">{row.label}</th>
                      <td className="px-3 py-2 text-gray-700 text-[13px] leading-snug">{row.value}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200">
                    <th className="text-left font-bold text-navy px-3 py-2 text-[13px]">Website</th>
                    <td className="px-3 py-2 text-[13px]">
                      <Link to="/" className="text-blue-700 hover:underline inline-flex items-center gap-1">
                        girishkumarnaidu <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-8 lg:order-1">
            {/* Table of contents */}
            <div className="inline-block border border-gray-300 bg-paper px-6 py-4 mb-10" data-testid="bio-toc">
              <p className="font-bold text-navy text-center mb-2 font-serif">{tr("page.bio.contents")}</p>
              <ol className="space-y-1 text-sm">
                {sections.map((s, i) => (
                  <li key={`${s.heading}-${i}`}>
                    <a href={`#${slugify(s.heading)}`} className="text-blue-700 hover:underline">
                      <span className="font-bold mr-2">{i + 1}</span>{heading(s)}
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            {/* Sections */}
            {sections.map((s, i) => (
              <section key={`${s.heading}-${i}`} id={slugify(s.heading)} className="scroll-mt-28 mb-10" data-testid={`bio-section-${i}`}>
                <h2 className="font-serif text-2xl md:text-[1.7rem] text-navy border-b border-gray-300 pb-1.5 mb-4">
                  <span className="text-gray-400 mr-3 text-xl">{i + 1}</span>{heading(s)}
                </h2>
                {content(s).split(/\n\n+/).map((para, j) => (
                  <p key={`${para.slice(0, 24)}-${j}`} className="font-serif text-[15.5px] leading-[1.8] text-gray-800 mb-4 whitespace-pre-line">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            {/* Wiki-style footer note */}
            <div className="border-t border-gray-300 pt-4 mt-12 text-xs text-gray-400 font-serif italic">
              {tr("page.bio.footer")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
